import { camelCaseToTitleCaseSpecial } from '@/helpers/camelCaseToTitleCase';
import { Box, Icon, Accordion } from '@palmetto/palmetto-components';
import React, { useState, useCallback } from 'react';

import { DocumentRendererProps } from './DocumentRendererRegistry';
import { ModelOutputStatusBadge } from './ModelOutputStatusBadge';

const refreshCooldownMs = 45000;

const sectionLabelMap: Record<string, string> = {
  shadeReport: 'Solar Arrays',
  utilityBill: 'Utility Bill',
  ptoConfirmation: 'Permission to Operate',
  planSet: 'Plan Set',
};

interface FieldValueProps {
  label: string;
  value: any;
  expectedValue: any;
  isNested?: boolean;
  indentLevel?: number;
}

const FieldValue: React.FC<FieldValueProps> = ({ label, value, expectedValue, indentLevel = 0 }) => {
  const renderValue = (val: any, currentIndentLevel = indentLevel): React.ReactNode => {
    if (val === null || val === undefined || val === '' || val === 'null') {
      return <Box></Box>;
    }
    const valType = typeof val;

    if (valType === 'boolean') {
      return <Box fontSize="sm">{val.toString()}</Box>;
    }

    if (valType === 'number' || valType === 'string') {
      return <Box fontSize="sm">{val}</Box>;
    }

    if (Array.isArray(val)) {
      if (val.length === 0) {
        return <Box></Box>;
      }
      return (
        <Box>
          {val.map((item, index) => (
            <Box key={index} alignItems="flex-start" gap="sm">
              <Box>{renderValue(item, currentIndentLevel + 1)}</Box>
            </Box>
          ))}
        </Box>
      );
    }

    if (valType === 'object') {
      return <ModelOutputResponse response={val} isNested indentLevel={currentIndentLevel + 1} />;
    }

    return <Box fontSize="sm">{String(val)}</Box>;
  };

  return (
    <Box gap="sm" margin="sm sm md sm">
      <Box direction="row" justifyContent="space-between">
        <Box fontSize="sm" fontWeight="bold">
          {camelCaseToTitleCaseSpecial(label)}
        </Box>
        <Box>
          {expectedValue !== undefined && (
            <Box fontSize="sm">
              {`${value}`.toLowerCase() == `${expectedValue}`.toLowerCase() ? (
                <Icon name="check" color="success-500" />
              ) : (
                <Icon name="remove" color="danger-500" />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box direction="row">
        <Box width="50%" alignContent="flex-start">
          {renderValue(expectedValue)}
        </Box>
        <Box width="50%" alignContent="flex-start">
          {renderValue(value)}
        </Box>
      </Box>
    </Box>
  );
};

interface ModelOutputResponseProps {
  response: Record<string, any>;
  expectedOutput?: Record<string, any>;
  isNested?: boolean;
  indentLevel?: number;
}

const ModelOutputResponse: React.FC<ModelOutputResponseProps> = ({
  response,
  expectedOutput,
  isNested = false,
  indentLevel = 0,
}) => {
  if (!response || Object.keys(response).length === 0) {
    return (
      <Box color="grey-500" fontSize="sm">
        No data available
      </Box>
    );
  }

  // Filter out documentLayout from the response data
  const filteredResponse = Object.fromEntries(Object.entries(response).filter(([key]) => key !== 'documentLayout'));

  const content = (
    <>
      {Object.entries(filteredResponse).map(([key, value]) => (
        <FieldValue
          key={key}
          label={key}
          value={value}
          isNested={isNested}
          indentLevel={indentLevel}
          expectedValue={expectedOutput?.[key]}
        />
      ))}
    </>
  );

  if (isNested) {
    return (
      <Box
        borderWidth="xs 0 0 0"
        borderColor="grey-200"
        radius="sm"
        padding="md"
        margin={indentLevel > 1 ? 'lg 0 0 0' : '0'}
      >
        {content}
      </Box>
    );
  }

  return <Box>{content}</Box>;
};

export const DefaultDocumentRenderer: React.FC<DocumentRendererProps> = ({
  modelOutput,
  isOpen,
  onToggle,
  onRefresh,
}) => {
  const documentType = modelOutput.references?.documentType ?? modelOutput.references?.documentTypes?.[0];
  const [lastRefreshedAt, setLastRefreshedAt] = useState<number | null>(null);
  const canRefresh = lastRefreshedAt === null || Date.now() - lastRefreshedAt >= refreshCooldownMs;

  const handleRefresh = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (canRefresh) {
        onRefresh?.();
        setLastRefreshedAt(Date.now());
      }
    },
    [canRefresh, onRefresh],
  );

  return (
    <Accordion.Panel key={modelOutput._id?.toString()} isOpen={isOpen}>
      <Accordion.PanelSummary hasCaret="right" onToggle={onToggle} isDetailsOpen={isOpen}>
        <Box direction="row" justifyContent="space-between" width="100%" gap="lg" alignItems="baseline">
          <Box fontSize="sm" fontWeight="bold">
            {documentType ? sectionLabelMap[documentType] || documentType : 'Document'}
          </Box>
          {modelOutput.status && modelOutput.status !== 'completed' && (
            <ModelOutputStatusBadge
              status={modelOutput.status}
              attemptCount={modelOutput.attemptCount}
              failureReason={modelOutput.failureReason}
              claimedAt={modelOutput.claimedAt ? new Date(modelOutput.claimedAt) : undefined}
            />
          )}
          {modelOutput.expectedOutputs && modelOutput.score > 0 && (
            <Box fontSize="sm" fontWeight="medium" justifyContent="flex-end">
              {modelOutput.score === 1 ? (
                <Icon name="check" color="success-500" />
              ) : (
                `${(modelOutput.score * 100).toFixed(2)} % Match`
              )}
            </Box>
          )}
        </Box>
      </Accordion.PanelSummary>
      <Accordion.PanelDetails>
        <Box padding="xs">
          {modelOutput.status !== 'completed' && modelOutput.status !== 'unretryable' && (
            <div className="flex justify-end">
              <a
                href="#"
                onClick={handleRefresh}
                className={`text-xs ${canRefresh ? 'cursor-pointer text-primary-500' : 'pointer-events-none cursor-not-allowed text-text-tertiary'}`}
                aria-disabled={!canRefresh}
              >
                Refresh status
              </a>
            </div>
          )}
          {(modelOutput.status === 'completed' || modelOutput.response) && (
            <ModelOutputResponse response={modelOutput.response} expectedOutput={modelOutput.expectedOutputs} />
          )}
          {modelOutput.status !== 'completed' && !modelOutput.response && modelOutput.expectedOutputs && (
            <ModelOutputResponse response={{}} expectedOutput={modelOutput.expectedOutputs} />
          )}
        </Box>
      </Accordion.PanelDetails>
    </Accordion.Panel>
  );
};
